import type { FC } from "react";
import React from "react";

import clsx from "clsx";

import { DateTime } from "luxon";

import { useTranslation } from "react-i18next";

import { Chip } from "shared/Components";
import type {
	AgeGenderLevelModel,
	FullTeamModel,
	GroupTeamModel,
	TournamentDivisionStageGroupTeamModel
} from "shared/types";
import { Text } from "shared/uikit";
import { SizesEnum, TextStylesEnum, TextVariantsEnum } from "shared/uikit/types";
import { renderGender } from "utils/renderGender";

import { getAgeCategoryYear } from "utils/serviceUtils/helpers";

interface MiddleTeamContentProps {
	wrapperClassName?: string;
	team: FullTeamModel | GroupTeamModel | AgeGenderLevelModel | TournamentDivisionStageGroupTeamModel;
	noMargin?: boolean;
	size?: SizesEnum.XS | SizesEnum.MD;
	limitLevelWidth?: boolean;
}

const MiddleTeamContent: FC<MiddleTeamContentProps> = ({
	wrapperClassName = "",
	team,
	noMargin = false,
	size = SizesEnum.MD,
	limitLevelWidth
}) => {
	const { t } = useTranslation();

	return (
		<div
			className={clsx(
				"flex items-center",
				wrapperClassName,
				noMargin ? "m-0" : "mb-1",
				size === SizesEnum.XS ? "gap-0.5" : "gap-2"
			)}
		>
			<Chip
				size={size}
				text={
					<div className="flex items-baseline">
						<Text
							style={TextStylesEnum.Hint}
							variants={size === SizesEnum.XS ? TextVariantsEnum.Caption2 : TextVariantsEnum.Caption3}
						>
							{(team as FullTeamModel).age?.value ||
								(team as FullTeamModel)?.age?.structEntry?.value ||
								DateTime.fromJSDate(
									new Date((team as FullTeamModel).age?.structEntry?.day || (team as FullTeamModel)?.age?.date || "")
								)?.month}
						</Text>
						<Text
							style={TextStylesEnum.Hint}
							variants={size === SizesEnum.XS ? TextVariantsEnum.Caption2 : TextVariantsEnum.Caption}
						>
							{`/${getAgeCategoryYear((team as FullTeamModel).age)}`}
						</Text>
					</div>
				}
			/>
			<Chip
				size={size}
				text={
					<div className="flex items-baseline">
						<Text
							style={TextStylesEnum.Hint}
							variants={size === SizesEnum.XS ? TextVariantsEnum.Caption2 : TextVariantsEnum.Caption3}
						>
							{renderGender(team?.gender?.value || null, t)}
						</Text>
					</div>
				}
			/>
			<Chip
				size={size}
				text={
					<div className={clsx("flex items-baseline", limitLevelWidth && "max-w-[68px]")}>
						<Text
							style={TextStylesEnum.Hint}
							variants={size === SizesEnum.XS ? TextVariantsEnum.Caption2 : TextVariantsEnum.Caption3}
							className="whitespace-nowrap overflow-hidden text-ellipsis"
						>
							{`${team.level?.name} (${(team.level?.ord || 0) + 1})`}
						</Text>
					</div>
				}
			/>
		</div>
	);
};

export default MiddleTeamContent;
